import { useRecipeHistoryService } from "../../Hooks/useRecipeHistoryService";
import { PageHeader } from "../../Shared/PageHeader";
import PageOverlay from "../../Shared/PageOverlay";
import YesNoModal from "../../Shared/YesNoModal";
import { RecipeInfo } from "../RecipeView/Components/RecipeInfo";
import React, { useState, useEffect } from "react";
import { Paper, Typography, Button } from "@material-ui/core";
import { Link } from "react-router-dom";
import queryString from "query-string";
import RestoreIcon from "@material-ui/icons/Restore";
import ArrowBackIcon from "@material-ui/icons/ArrowBack";

export function RecipeHistoryView(props) {
	const recipeHistoryService = useRecipeHistoryService(props.config);
	const [historyEntry, setHistoryEntry] = useState({ placeholder: true });
	const [isLoading, setIsLoading] = useState(true);
	const [scale, setScale] = useState("1");
	const [system, setSystem] = useState("");
	const [convertToMass, setConvertToMass] = useState(false);
	const [nextChangeDate, setNextChangeDate] = useState(null);
	const [isRestoreModalOpen, setIsRestoreModalOpen] = useState(false);
	const [isRestoring, setIsRestoring] = useState(false);

	const recipeId = props.match.params.recipeId;
	const recipeHistoryId = props.match.params.recipeHistoryId;

	useEffect(() => {
		document.title = `Recipe History - ${props.config.appName}`;
		setIsLoading(true);

		const queryStringValues = queryString.parse(props.location.search);
		if (queryStringValues.scale) setScale(queryStringValues.scale);
		if (queryStringValues.system) setSystem(queryStringValues.system);
		if (queryStringValues.convertToMass) setConvertToMass(queryStringValues.convertToMass === "1");

		recipeHistoryService.getHistoryEntry(recipeHistoryId, (response) => {
			if (response && response.data) {
				const entry = response.data;
				// Convert history entry to recipe format
				const recipeFromHistory = {
					recipeId: entry.recipeId,
					name: entry.name,
					ingredients: entry.ingredients,
					ingredientsList: entry.ingredientsList,
					instructions: entry.instructions,
					notes: entry.notes,
					createDateTime: entry.createDateTime,
					updateDateTime: entry.updateDateTime,
					imageOrderCsv: entry.imageOrderCsv,
					tags: []
				};
				setHistoryEntry(recipeFromHistory);
				setNextChangeDate(entry.recipeHistoryDateTime);
				document.title = `${entry.name} (History) - ${props.config.appName}`;
			}
			setIsLoading(false);
		}, (error) => {
			console.error(error);
			if (error.response && error.response.status === 404) {
				props.history.push("/notfound");
			}
			setIsLoading(false);
		});
	}, [recipeHistoryId]); // eslint-disable-line react-hooks/exhaustive-deps

	const formatDateTime = (dateTimeString) => {
		if (!dateTimeString) return "";
		const date = new Date(dateTimeString);
		return date.toLocaleDateString();
	};

	const getActiveRangeText = () => {
		if (!historyEntry.updateDateTime || !nextChangeDate) return "";
		const startDate = formatDateTime(historyEntry.updateDateTime);
		const endDate = formatDateTime(nextChangeDate);
		return `Version ${recipeHistoryId} - Active from ${startDate} to ${endDate}`;
	};

	const handleRestoreClick = () => {
		setIsRestoreModalOpen(true);
	};

	const handleRestoreConfirm = () => {
		setIsRestoreModalOpen(false);
		setIsRestoring(true);

		recipeHistoryService.restoreHistoryEntry(recipeHistoryId, (response) => {
			setIsRestoring(false);
			// Navigate to the recipe page after successful restore
			props.history.push(`/recipes/${recipeId}`);
		}, (error) => {
			console.error(error);
			setIsRestoring(false);
			alert("Failed to restore this version. Please try again.");
		});
	};

	const handleRestoreCancel = () => {
		setIsRestoreModalOpen(false);
	};

	const imageUrls = [];
	if (historyEntry.imageOrderCsv) {
		const imageIds = historyEntry.imageOrderCsv.split(',');
		imageIds.forEach(id => {
			if (id) {
				imageUrls.push(`${props.config.apiUrl}/Recipes/${recipeId}/Images/${id}`);
			}
		});
	}

	return (
		<React.Fragment>
			<PageHeader text={historyEntry.name} subText={getActiveRangeText()} config={props.config} />
			<PageOverlay showOverlay={isLoading || isRestoring} />
			<div style={{ marginBottom: 12 }}>
				<Button
					component={Link}
					to={`/recipes/${recipeId}/history`}
					variant="contained"
					color="primary">
					<ArrowBackIcon style={{ marginRight: 8 }} />
					Back to History
				</Button>
			</div>
			{!historyEntry.placeholder ? (
				<Paper className="print-no-padding" style={{ padding: 12 }}>
					<RecipeInfo
						recipe={historyEntry}
						imageUrls={imageUrls}
						scale={scale}
						setScale={setScale}
						system={system}
						setSystem={setSystem}
						convertToMass={convertToMass}
						setConvertToMass={setConvertToMass}
						setOwnerBlurb={() => {}} />
					<div style={{ marginTop: 20 }} className="no-print">
						<Button
							component={Link}
							to={`/recipes/${recipeId}`}
							variant="outlined"
							style={{ marginRight: 10 }}>
							View Current Recipe
						</Button>
						<Button
							variant="outlined"
							color="secondary"
							onClick={handleRestoreClick}
							disabled={isRestoring}
							startIcon={<RestoreIcon />}>
							Restore This Version
						</Button>
					</div>
				</Paper>
			) : (
				<Paper style={{ padding: 12 }}>
					<Typography>Loading...</Typography>
				</Paper>
			)}
			<YesNoModal
				isOpen={isRestoreModalOpen}
				title="Restore This Version"
				question={(
					<span>
						Are you sure you want to restore this version?
						<br />
						The current recipe will be saved to history before restoring.
					</span>
				)}
				onYes={handleRestoreConfirm}
				onNo={handleRestoreCancel} />
		</React.Fragment>
	);
}
